/**
 * 전자서명 관련 API 훅
 * 전자서명 관련 기능을 제공하는 커스텀 훅입니다.
 *
 * 주요 기능:
 * - 전자서명 목록 및 상세 조회
 * - 템플릿 목록 및 상세 조회
 * - 전자서명 미리보기
 * - 전자서명 확인/취소
 * - 파트너 전자서명 처리
 */

import { useCallback } from "react";
import {
  cancelEsign_RCS23,
  confirmEsign_RCS21,
  getEsignonDetail_RCS16,
  getEsignonList_RCS30,
  getEsignonTemplateDetail_RCS11,
  getEsignonTemplateList_RCS10,
  getEsingMember,
  getWriteDataPreview_RCS22,
  partnerEsign_RCS02,
} from "src/api/contract/contract-api";
import { TemplateInfo } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";

/** 기본 반환 객체 */
const rtn: any = { validationPass: false, message: "", response: {} };

/**
 * 전자서명 관련 API 훅
 * 전자서명 처리를 위한 다양한 API 호출 함수를 제공합니다.
 */
const useEsignApi = () => {
  /** 전자서명 목록 조회 API */
  const { executeAsync: getEsignList } = useApiOperation(getEsignonList_RCS30);
  /** 전자서명 상세 조회 API */
  const { executeAsync: getEsignDetail } = useApiOperation(getEsignonDetail_RCS16, { noLoading: true });
  /** 전자서명 템플릿 목록 조회 API */
  const { executeAsync: getEsignTemplateList } = useApiOperation(getEsignonTemplateList_RCS10);
  /** 전자서명 템플릿 상세 조회 API */
  const { executeAsync: getEsignTemplateDetail } = useApiOperation(getEsignonTemplateDetail_RCS11, { noLoading: true });
  /** 전자서명 회원 정보 조회 API */
  const { executeAsync: getEsignMemeberInfo } = useApiOperation(getEsingMember);
  /** 전자서명 미리보기 데이터 조회 API */
  const { executeAsync: getWriteDataPreview } = useApiOperation(getWriteDataPreview_RCS22);
  /** 전자서명 확인 API */
  const { executeAsync: confirmSign } = useApiOperation(confirmEsign_RCS21);
  /** 전자서명 취소 API */
  const { executeAsync: cancelSign } = useApiOperation(cancelEsign_RCS23);
  /** 파트너 전자서명 API */
  const { executeAsync: partnerSign } = useApiOperation(partnerEsign_RCS02);

  /**
   * 전자서명 상세 정보 조회
   * @param contractId - 계약 ID
   * @param workflowId - 워크플로우 ID
   * @returns 전자서명 워크플로우 정보
   */
  const getDetail = useCallback(
    async (contractId: number, workflowId: number) => {
      const sendData = { contractId: Number(contractId)!, workflowId: Number(workflowId)! };
      const response2 = await getEsignDetail(sendData!);
      if (response2.data.data.workflowInfo) {
        return response2.data.data.workflowInfo;
      }
      return {};
    },
    [getEsignDetail],
  );

  /**
   * 전자서명 목록 조회
   * @param contractId - 계약 ID
   * @returns 전자서명 목록 데이터
   */
  const getList = useCallback(
    async (contractId: number) => {
      try {
        const response = await getEsignList({ contractId });
        if (response?.data?.data?.contractESign) {
          return response.data.data.contractESign;
        }
        return [];
      } catch (error) {
        console.error("전자서명 목록 조회 오류:", error);
        return [];
      }
    },
    [getEsignList],
  );

  /**
   * 전자서명 템플릿 상세 조회
   * @param contractId - 계약 ID
   * @param templateId - 템플릿 ID
   * @returns 템플릿 상세 정보
   */
  const getTemplateDetail = useCallback(
    async (contractId: number, templateId: number) => {
      const sendData = { contractId: Number(contractId)!, templateId: Number(templateId)! };
      const response2 = await getEsignTemplateDetail(sendData!);
      if (response2.data.data.templateInfo) {
        return response2.data.data.templateInfo;
      }
      return {};
    },
    [getEsignTemplateDetail],
  );

  /**
   * 전자서명 템플릿 목록 조회
   * @param contractId - 계약 ID
   * @returns 템플릿 목록
   */
  const getTemplateList = useCallback(
    async (contractId: number) => {
      let returnArr: Array<TemplateInfo> = [];
      const response = await getEsignTemplateList({ contractId });

      if (response.data.data.templateList) {
        returnArr = response.data.data.templateList.template;
      }

      return returnArr;
    },
    [getEsignTemplateList],
  );

  /**
   * 전자서명 회원 정보 조회
   * @returns 회원 정보
   */
  const getMemberInfo = useCallback(async () => {
    return await getEsignMemeberInfo();
  }, [getEsignMemeberInfo]);

  /**
   * 전자서명 미리보기 데이터 조회
   * @param params - 미리보기 파라미터
   * @returns 미리보기 URL
   */
  const getWritedPreviewData = useCallback(
    async (params: any) => {
      const response: any = await getWriteDataPreview(params);
      if (response.data.data.previewWorkflow) {
        return response.data.data.previewWorkflow.previewUrl;
      }
    },
    [getWriteDataPreview],
  );

  /**
   * 전자서명 확인 처리
   * @param params - 확인 처리 파라미터
   * @returns 워크플로우 시작 결과
   */
  const confirmEsignData = useCallback(
    async (params: any) => {
      const response: any = await confirmSign(params);
      if (response.data.data) {
        return response.data.data.startWorkflow;
      }
    },
    [confirmSign],
  );

  /**
   * 전자서명 취소 처리
   * @param params - 취소 처리 파라미터
   * @returns API 응답 결과
   */
  const cancelEsignData = useCallback(
    async (params: any) => {
      return await cancelSign(params);
    },
    [cancelSign],
  );

  /**
   * 파트너 전자서명 데이터 조회
   * @returns API 응답 결과
   */

  // 파트너 전자서명 가능 여부 조회
  const getPartnerEsignCheck = async () => {
    const { data } = await partnerSign();
    const { isESignonAvailable, isModuSignAvailable } = data.data;
    return { isESignonAvailable, isModuSignAvailable };
  };

  return {
    getList,
    getDetail,
    getTemplateDetail,
    getTemplateList,
    getMemberInfo,
    getWritedPreviewData,
    confirmEsignData,
    cancelEsignData,
    getPartnerEsignCheck,
  };
};

export default useEsignApi;
